<template>

<div class="list-view no-sidebar">

    <loading v-if="loading"></loading>

	<div class="detail no-sub-header two-columns" v-else-if="!editingDisposal">

		<div class="section">
			<h2>Disposition Info</h2>
			<div class="two-columns">
				<label-required label="Date Disposed" :condition="entry.disposal_date"></label-required>
				<div></div>
				<input type="date" v-model="entry.disposal_date" style="width: 11em;">
				<div v-if="disposeFrom4473" class="notes right"><span class="inline-link" @click="switchDisposalSearch">Dispose from serial number search?</span></div>
				<div v-else class="notes right"><span class="inline-link" @click="switchDisposalSearch">Dispose from an eligible 4473?</span></div>
			</div>
			<template v-if="disposeFrom4473">
				<label-required label="Disposed To" :condition="$fullName(contact)"></label-required>
				<div class="input-with-control">
					<input type="text" v-model="search_4473" placeholder="Search eligible 4473s..." @focus="showLogBookFormList = true">
					<div :class="['button', search_4473 ? 'action' : 'disabled', 'input-go']"><i class="fas fa-search"></i></div>
				</div>
				<div v-if="showLogBookFormList" class="search-result bordered">
					<ul v-if="filteredLogBookForms && filteredLogBookForms.length != 0" class="search-list">
						<li class="clickable-list-item" v-for="(logBookForm, index) in filteredLogBookForms" @click="assignDisposal(logBookForm, true)">
							<span>{{ logBookForm.version > 1 ? $fullName(logBookForm.form_data.section_b) : $fullName(logBookForm.form_data) }}</span>
							<span>{{ logBookForm.version > 1 ? logBookForm.form_data.section_b.address_1 : logBookForm.form_data.address_1 }}</span>
							<span>{{ logBookForm.nics_number }}</span>
							<span>{{ logBookForm.version > 1 ? logBookForm.form_data.section_b.city : logBookForm.form_data.city }}, {{ logBookForm.version == 2 ? logBookForm.form_data.section_b.state : logBookForm.form_data.state }} {{ logBookForm.version == 2 ? logBookForm.form_data.section_b.zip_code : logBookForm.form_data.zip_code }}</span>
						</li>
					</ul>
					<ul v-else class="search-list no-results">
						<li>No eligible 4473s found.</li>
					</ul>
				</div>
				<div class="search-result bordered">
					{{ contact.full_name || $fullName(contact) }}<br>
					{{ contact.address_1 }}<br>
					<template v-if="contact.address_2">{{ contact.address_2 }}<br></template>
					{{ entry.city }}<span v-if="entry.city">,</span> {{ entry.state }} {{ contact.zip_code }}<br>
					<template v-if="contact.ffl_number">FFL: {{ contact.ffl_number }}</template>
				</div>
			</template>
			<template v-else>
				<div class="three-one-columns">
					<div>
						<label-required label="Disposed To" :condition="$fullName(contact)"></label-required>
						<div v-if="!showFFLSearch" class="input-with-control">
							<input type="text" v-model="search_disposals" placeholder="Search dispositions..." @keyup.enter="searchDisposals()" v-focus>
							<div :class="['button', search_disposals ? 'action' : 'disabled', 'input-go']" @click="searchDisposals()"><i class="fas fa-search"></i></div>
						</div>
						<ffl-number-search v-if="showFFLSearch" @searchResult="setList" />
					</div>
					<div>
						<button class="neutral smaller full-width" style="margin: 0.66em 0 0.33em 0;" @click="showFFLSearch = !showFFLSearch">Search By {{ showFFLSearch ? 'Name' : 'FFL' }}</button>
						<button class="neutral smaller full-width" @click="newDisposedTo()">Create Disposed To</button>
					</div>
				</div>

				<div class="search-result bordered">
                    <loading v-if="searchingDisposals"></loading>
					<ul v-else-if="disposals && disposals.length != 0" class="search-list">
						<li class="clickable-list-item" v-for="(disposal, index) in disposals" @click="assignDisposal(disposal)">
							<span>{{ disposal.full_name || $fullName(disposal) }}</span>
							<span>{{ disposal.address_1 }}</span>
							<span>{{ disposal.ffl_number }}</span>
							<span>{{ disposal.city }}<span v-if="disposal.city">,</span> {{ disposal.state }} {{ disposal.zip_code }}</span>
						</li>
					</ul>
					<ul v-else-if="disposalSearchMessage" class="search-list no-results">
						<li>{{ disposalSearchMessage }}</li>
					</ul>
					<template v-else>
						{{ contact.full_name || $fullName(contact) }}<br>
						{{ contact.address_1 }}<br>
						<template v-if="contact.address_2">{{ contact.address_2 }}<br></template>
						{{ contact.city }}<span v-if="contact.city">,</span> {{ contact.state }} {{ contact.zip_code }}<br>
						<template v-if="contact.ffl_number">FFL: {{ contact.ffl_number }}</template>
                        <div v-if="contact.client_id == client_id && contact.contact_id != getClient.contact_id" class="controls" style="position: absolute; bottom: -0.75em; right: 3px;">
							<button class="neutral" @click="editingDisposal = true">Edit</button>
						</div>
                    </template>
				</div>

			</template>
		</div>

		<div class="section">
			<h2>Serial Numbers</h2>
			<label-required label="Enter one or more serial numbers for this disposition" :condition="serial_numbers[0] != ''"></label-required>
			<div class="two-columns">
				<div v-for="(serial_number, index) in serial_numbers" class="input-with-control">
					<input type="text" @keyup.enter="searchSerialNumbers(index)" v-model="serial_numbers[index]['serial_number']" :ref="'serial_number_' + index">
					<div class="button action input-go" @click="searchSerialNumbers(index)"><i class="fas fa-search"></i></div>
					<label v-if="serial_number['sequence_number']" style="font-size: 1em; margin: 0.25em 0 1em 0.25em;">
						Sequence Number: {{ serial_number['ffl_log_book_code'] + '-' + serial_number['sequence_number'] }}<br>
						Mfg: {{ serial_number['manufacturer'] }}<br>
						Model: {{ serial_number['model'] }}
					</label>
				</div>
			</div>

			<div v-if="serialNumberSearchResults.length > 0 || serialNumberSesarchMessage" class="search-result bordered">
				<div class="controls" style="top: -10px; right: -16px;"><button class="button action" @click="cancelSerialNumberSearchResults()">X</button></div>
				<ul v-if="serialNumberSearchResults.length > 0" class="search-list" style="margin: 0 3em 0 0;">
					<li class="clickable-list-item" v-for="(entry, index) in serialNumberSearchResults" @click="assignSerialNumber(entry)">
						<span>Sequence: <b>{{ entry.ffl_log_book_code }}-{{ entry.sequence_number }}</b></span>
						<span>{{ entry.manufacturer }} {{ entry.model }}</span>
						<span>Serial Number: <b>{{ entry.serial_number }}</b></span>
						<span>{{ entry.gun_type }} {{ entry.caliber }}</span>
					</li>
				</ul>
				<ul v-else class="search-list no-results">
					<li>{{ serialNumberSesarchMessage }}</li>
				</ul>
			</div>

			<div v-else>
				<button :class="[ 'spaced', serial_numbers.length > 1 ? 'action' : 'disabled' ]" @click="removeSerialInput()">-</button>
				<button :class="serial_numbers[serial_numbers.length - 1].serial_number == '' ? 'disabled' : 'action'" @click="addSerialInput()">+</button>
			</div>

			<div class="section_dead" v-if="sectionsComplete < 1"></div>

		</div>

		<div class="section">
			<h2>Dispose With</h2>
			<div class="three-columns">
				<label>NICS Number or CCW</label>
				<label></label>
				<label>Repair Number</label>
			</div>
			<div class="three-columns">
				<input type="text" v-model="entry.nics_number">
				<div class="checkbox-group">
					<span :class="['pointer', 'far', nics_not_required ? 'fa-check-square' : 'fa-square']" @click="toggleNICSNotRequired()"></span>
					<label>NICS not required</label>
				</div>
				<input type="text" v-model="entry.repair_number">
			</div>
			<label>Note</label>
			<textarea v-model="entry.notes"></textarea>
			<div class="section_dead" v-if="sectionsComplete < 2"></div>
		</div>

		<div class="section">

			<div style="width: 60%; margin: 0 0 3em 0;">
				<div class="input-with-control input-with-control-with-tip">
					<input type="text" v-model="entry.form_number" placeholder="Transaction Serial Number" @keyup.enter="searchFormNumbers()">
					<div class="button input-go action" @click="searchFormNumbers()"><i class="fas fa-search"></i></div>
					<span class="inline-tip fas fa-info-circle" @click="showToolTip = true"></span>
				</div>
				<div v-if="form_numbers.length > 0 || formNumberSearchMessage" class="search-result bordered" style="margin: 0 1em 0 0;">
					<div class="controls"><button class="button action" @click="form_numbers = []">X</button></div>
					<ul v-if="form_numbers.length > 0" class="search-list" style="margin: 0 3em 0 0;">
						<li class="clickable-list-item" v-for="(form_number, index) in form_numbers" @click="assignFormNumber(form_number)">
							{{ form_number }}
						</li>
					</ul>
					<ul v-else class="search-list no-results">
						<li>{{ formNumberSearchMessage }}</li>
					</ul>
				</div>
			</div>

			<div :class="['button', 'spaced', isDisposable && !dispositionComplete ? 'action' : 'disabled' ]" @click="saveDisposition()">Save Disposition</div>
			<button class="cancel" @click="clearData(true)">{{ dispositionComplete ? 'Clear' : 'Cancel' }}</button>
			<button v-if="dispositionComplete && entry.ffl_log_book_entry_id" :class="['action', { disabled: loadingLogBookEntry }]" style="margin: 0 0 0 0.5em;" @click="doPrint()">Print Disposition Receipt</button>

			<transition name="fade"><div class="message save-message" v-if="saveMessage">{{ saveMessage }}</div></transition>

			<div class="section_dead" style="height: 3em;" v-if="sectionsComplete < 3"></div>

		</div>

		<div class="modal" v-if="showModal">
			<div class="inner">
				<h2>{{ $caplitalizeWords(modalFor) }}</h2>
				<div class="content">
					<div v-if="modalStatus == 'error'" class="center" style="font-size: 1.5em; margin: 0.5em; color: gray;"><i class="fa fa-bell"></i></div>
					<div v-html="modalContent" style="margin: 1em;"></div>
					<div v-if="loadingModal" class="center" style="margin: 1em;"><i class="fa fa-spinner fa-spin"></i></div>
				</div>
				<div class="controls">
					<div class="button cancel spaced" @click="showModal = false">Got It</div>
					<div class="button action spaced" @click="goToForm()">Go To 4473</div>
				</div>
			</div>
		</div>

		<div class="modal" v-if="showToolTip">
			<div class="inner">
				<h2>Transaction Serial Number</h2>
				<div class="content">
					<p>You can enter the Transferor's / Seller's Transaction Serial Number from the 4473 associated with this disposal.</p>
					<p>This will help you link this disposal to that specific 4473 in your electronic or paper filing system.</p>
				</div>
				<div class="controls">
					<div class="button cancel" @click="showToolTip = false">Got It</div>
				</div>
			</div>
		</div>

	</div>

	<div v-else class="detail three-one-columns">
		<div class="detail-form">
			<h2>Disposed To Info</h2>
			<contact-detail-form :clientId="client_id" :contact="contact" @save="assignDisposal($event)" @cancel="editingDisposal = false"></contact-detail-form>
		</div>
		<div class="detail-stats list">
			<h2>Note</h2>
			<div v-if="contact.contact_id">
				<p>Editing this contact record will not affect previously saved log book entries. The contact will be updated for this and future dispositon records.</p>
			</div>
			<div v-else>
				<p>You are adding a new contact record that can be used for this and future disposition records.</p>
			</div>
		</div>
	</div>

	<help-popup :helpLink="{208:'Dispose Electronically', 209:'Dispose Manually', 210:'Correcting Logbook Errors'}" :tours="false"></help-popup>


</div>

</template>

<script>
import FFLNumberSearch from "@/components/FFLNumberSearch.vue"
import LabelRequired from '@/components/LabelRequired.vue'
import ContactDetailForm from '@/components/ContactDetailForm.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'logbooks-dispose',
	props: ['4473','id'],
    components: {
		'ffl-number-search': FFLNumberSearch,
        'label-required': LabelRequired,
        'contact-detail-form': ContactDetailForm,
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: false,
            searchingDisposals: false,
			client_id: null,
			disposeFrom4473: true,
			showLogBookFormList: false,
    		entry: {},
    		editingDisposal: false,
    		logBookForms: [],
    		logBookFormId: null,
    		serial_numbers: [{ serial_number: '' }],
			search_disposals: null,
			disposalSearchMessage: null,
			search_4473: null,
			disposals: [],
			saveMessage: null,
			contact: {
				notes: [{ note: '', priority: '' }]
			},
			ffl_log_book_form_id: '',
			searchingSerialNumberIndex: 0,
			serialNumberSearchResults: [],
			serialNumberSesarchMessage: '',
			showModal: false,
    		modalFor: '',
    		modalContent: '',
			loadingModal: false,
			modalStatus: '',
    		showToolTip: false,
			showFFLSearch: false,
    		form_numbers: [],
			formNumberSearchMessage: '',
			nics_not_required: '',
            dispositionComplete: false,
            loadingLogBookEntry: false,
            reloadDateInput: Math.random() // will re-render component on clearData()
		}
	},
    created() {
	    if (this.id) {
			this.disposeFrom4473 = false
		    this.getListSerialNumbers()
		    if (this.$route.query.disposal_contact_id) {
			    this.getDisposalById(this.$route.query.disposal_contact_id)
			}
	    } else {
	       	this.getListLogBookForms()
	    }
	    this.clearData()
		this.resetContact()
    },
    watch: {
		saveMessage(newVal) {
			if (newVal) {
				setTimeout(() => {
					this.saveMessage = null
				}, 3000)
			}
		},
		sectionsComplete(newVal) {
			if (newVal == 1) {
				this.$refs.serial_number[0].focus()
			}
		}

	},
    computed: {
		isDisposable() {
            let serialNumberFound = false
            for (let i = 0; i < this.serial_numbers.length; i++) {
                if (this.serial_numbers[i]['ffl_log_book_entry_id']) {
                    serialNumberFound = true
                    break;
                }
            }
			return serialNumberFound && (this.sectionsComplete > 2 || (this.sectionsComplete > 1 && this.contact.ffl_number))
		},
	    sectionsComplete() {
		    if (this.notEmpty(this.entry,['disposal_date']) && this.notEmpty(this.contact,['address_1','city','state','zip_code']) && this.serial_numbers[0] != '' && (this.notEmpty(this.entry,['nics_number']) || this.notEmpty(this.entry,['repair_number']) || this.notEmpty(this.entry,['notes'])) ) {
			    return 3
		    } else if (this.notEmpty(this.entry,['disposal_date']) && this.notEmpty(this.contact,['address_1','city','state','zip_code']) && this.serial_numbers[0] != '') {
			    return 2
			} else if (this.notEmpty(this.entry,['disposal_date']) && this.notEmpty(this.contact,['address_1','city','state','zip_code'])) {
				return 1
			} else {
			    return 0
			}
		},
    	filteredLogBookForms() {
    		var filteredLogBookForms;
    		if (this.search_4473 && this.logBookForms.length != 0) {
    			filteredLogBookForms = this.logBookForms.filter(logBookForm => {
    				if (logBookForm.version > 1) {
						return ((logBookForm.ffl_log_book_form_id == parseInt(this.search_4473) > -1) ||
    					(logBookForm.form_data.section_b.first_name.toLowerCase().indexOf(this.search_4473.toLowerCase()) > -1) ||
    					(logBookForm.form_data.section_b.last_name.toLowerCase().indexOf(this.search_4473.toLowerCase()) > -1) ||
    					(logBookForm.form_data.section_b.address_1.toLowerCase().indexOf(this.search_4473.toLowerCase()) > -1) ||
    					(logBookForm.form_data.section_b.city.toLowerCase().indexOf(this.search_4473.toLowerCase()) > -1) ||
    					(logBookForm.form_data.section_b.state.toLowerCase().indexOf(this.search_4473.toLowerCase()) > -1) ||
    					(logBookForm.form_data.section_b.zip_code.indexOf(this.search_4473) > -1) ||
    					(logBookForm.nics_number.indexOf(this.search_4473) > -1))
					} else {
						return false
					}
    			})
    			return filteredLogBookForms
    		} else {
    			return this.logBookForms
    		}
    	}
	},
	methods: {
		toggleNICSNotRequired() {
			this.nics_not_required = !this.nics_not_required
			if (this.nics_not_required) {
				this.entry.notes = 'NICS not required'
			} else {
				this.entry.notes = ''
			}
		},
		notEmpty(object, elements) {
			for (var i in elements) {
				if (typeof object[elements[i]] == 'undefined' || object[elements[i]] == null || object[elements[i]] == "") {
					return false
				}
			}
			return true
		},
		switchDisposalSearch() {
			this.entry = {
	    		ffl_log_book_entry_id: null,
	    		disposal_date: '',//this.$today().clone().format('YYYY-MM-DD'),
	    		disposal_contact_id: null,
	    		nics_number: null,
	    		form_number: null,
	    		repair_number: null,
				notes: null,
				product_id: null
			}
            this.nics_not_required = ''
            this.reloadDateInput = Math.random()
    		this.serial_numbers = [{ serial_number: '' }]
			this.contact = {
				first_name: null,
                middle_name: null,
				last_name: null,
                suffix: null,
				company_name: null,
				address_1: null,
				address_2: null,
				city: null,
				state: null,
				zip_code: null,
				ffl_number: null,
				notes: [{ note: '', priority: '' }]
			}
			this.disposeFrom4473 = !this.disposeFrom4473
			this.ffl_log_book_form_id = ''
		},
		searchSerialNumbers(index) {
			this.searchingSerialNumberIndex = index
			if (this.serial_numbers[this.searchingSerialNumberIndex]) {
				let searchTerm = this.serial_numbers[this.searchingSerialNumberIndex]['serial_number']
				let excludeFromSearch = []
				for (var i = 0; i < this.serial_numbers.length; i++) {
					if (this.serial_numbers[i] != this.serial_numbers[this.searchingSerialNumberIndex]) {
						excludeFromSearch.push(this.serial_numbers[i])
					}
				}
				this.apiCall({ destination: 'get_list_serial_numbers', data: { on_hand: 1, search_term: searchTerm, exclude: excludeFromSearch }}).then(obj => {
					this.client_id = obj.client_id
					if (obj.status == 'error') {
						this.serialNumberSesarchMessage = obj.message
					} else if (obj.serial_numbers.length > 1) {
						this.serialNumberSearchResults = obj.serial_numbers
						if (obj.form_number && !this.form.form_number) {
							this.form.form_number = obj.form_number
						}
					} else if (obj.serial_numbers.length == 1) {
						this.serial_numbers.splice(this.searchingSerialNumberIndex, 1, obj.serial_numbers[0])
						this.serialNumberSesarchMessage = ''
						this.serialNumberSearchResults = []
						this.addSerialInput()
					} else {
						this.serialNumberSesarchMessage = 'No matching serial numbers found'
					}
					if (obj.form_number && !this.form.form_number) {
						this.form.form_number = obj.form_number
					}
				})
			}
		},
		assignSerialNumber(serialNumber) {
			this.serial_numbers[this.searchingSerialNumberIndex] = serialNumber
			this.serialNumberSesarchMessage = ''
			this.serialNumberSearchResults = []
			if (this.searchingSerialNumberIndex == this.serial_numbers.length - 1) {
				this.addSerialInput()
			}
		},
		cancelSerialNumberSearchResults() {
			this.serial_numbers[this.searchingSerialNumberIndex]['serial_number'] = ''
			this.serialNumberSearchResults = []
		},
		addSerialInput() {
			// if the last serial number isn't already a blank input
			if (this.serial_numbers[this.serial_numbers.length - 1].serial_number != '') {
				this.serial_numbers.push({ serial_number: '' })
				// wait a bit for the new input to register in the DOM
				setTimeout(() => {
					let index = 'serial_number_' + (this.serial_numbers.length - 1)
					this.$refs[index][0].focus()
				}, 100)
			}
		},
		removeSerialInput() {
			if (this.serial_numbers.length > 1) {
				this.serial_numbers.pop()
			}
		},
		searchDisposals() {
            this.searchingDisposals = true
			this.disposals = null
			this.disposalSearchMessage = null
			this.apiCall({ destination: 'get_list_disposals', data: { source: 'LOGBOOKS', search_term: this.search_disposals, search_all_contacts: true }}).then(obj => {
                this.searchingDisposals = false
				this.client_id = obj.client_id
				if (obj.status == 'error') {
					this.disposalSearchMessage = obj.message
				} else if (obj.disposals.length > 1) {
					this.disposals = obj.disposals
				} else if (obj.disposals.length == 1) {
					this.assignDisposal(obj.disposals[0])
				} else {
					this.disposalSearchMessage = 'nothing found'
				}
			})
		},
		newDisposedTo() {
			this.disposals = []
			this.disposalSearchMessage = null
			this.resetContact()
			this.contact.client_id = this.client_id
			this.editingDisposal = true
		},
		getDisposalById(disposalId) {
			this.disposals = null
			this.disposalSearchMessage = null
			this.apiCall({ destination: 'get_list_disposals', data: { contact_id: disposalId }}).then(obj => {
				this.client_id = obj.client_id
				if (obj.status == 'error') {
					this.disposalSearchMessage = obj.message
				} else if (obj.disposals.length == 1) {
					this.assignDisposal(obj.disposals[0])
				} else {
					this.disposalSearchMessage = 'nothing found'
				}
			})
		},
		setList(list) {
			if (list.length > 1) {
				this.disposals = list
			} else if (list.length == 1) {
				this.apiCall({ destination: 'get_disposal_from_ffl_search', data: list[0] }).then(obj => {
					if (obj.status == 'success') {
						this.assignDisposal(obj.disposal_detail)
					} else {
						this.disposalSearchMessage = obj.message
					}
				}).catch(err => {
					console.log(err)
				})
			} else {
				this.disposalSearchMessage = 'No matching FFL found.'
			}
		},
		assignDisposal(disposal, isLogBookForm) {
			this.disposals = []
			this.search_disposals = null
			this.editingDisposal = false
			if (isLogBookForm) {
                // disposing from a 4473 with serial number(s)
				this.logBookFormId = disposal.ffl_log_book_form_id // will be used by goToForm
                if (disposal.version > 1) {
			        disposal.full_name = this.$fullName(disposal.form_data.section_b)
					this.populateDisposal(disposal)
    				// confirm there is at least one serial number recorded with this form
    				if (disposal.form_data.section_a && disposal.form_data.section_a.firearms_list) {
    					// TODO: call get_list_serial_numbers to get sequence numbers and log book entry ids
    					let serialNumbers = disposal.form_data.section_a.firearms_list.map(firearm => firearm.serial_number)
                        let fflLogBookEntryIds = disposal.form_data.section_a.firearms_list.map(firearm => firearm.ffl_log_book_entry_id)
    					this.apiCall({ destination: 'get_list_serial_numbers', data: { on_hand: 1, search_term: serialNumbers, ffl_log_book_entry_id: fflLogBookEntryIds, ffl_log_book_form_id: disposal.ffl_log_book_form_id }}).then(obj => {
    						//this.serial_numbers = obj.serial_numbers
                            if (obj.serial_numbers.length > 0) {
                                this.serial_numbers = obj.serial_numbers
                            } else {
        					    this.modalFor = 'No disposable serial numbers'
        					    this.modalContent = 'Name: ' + this.$fullName(disposal.form_data.section_b) + '<br><br>There are no disposable serial numbers associated with this 4473.<br><br>This could be because they have already been disposed.'
            					this.showModal = true
                            }
    					})
    				}
                } else {
			        disposal.full_name = this.$fullName(disposal.form_data)
					this.populateDisposal(disposal)
    				// confirm there is at least one serial number recorded with this form
    				if (disposal.form_data.section_d && disposal.form_data.section_d.firearms_list) {
    					// TODO: call get_list_serial_numbers to get sequence numbers and log book entry ids
    					let serialNumbers = disposal.form_data.section_d.firearms_list.map(firearm => firearm.serial_number)
    					this.apiCall({ destination: 'get_list_serial_numbers', data: { on_hand: 1, search_term: serialNumbers }}).then(obj => {
    						//this.serial_numbers = obj.serial_numbers
                            if (obj.serial_numbers.length > 0) {
                                this.serial_numbers = obj.serial_numbers
                            } else {
                                this.modalFor = 'No Section A serial numbers'
                                this.modalContent = 'Name: ' + this.$fullName(disposal.form_data) + '<br><br>There are no disposable serial numbers saved in Section D of this 4473.<br><br>This could be because they have already been disposed.'
            					this.showModal = true
                            }
    					})
    				}
                }
			} else {
				this.showLogBookFormList = false
				disposal.full_name = this.$fullName(disposal)
				this.contact = disposal
			}
        },
        populateDisposal(disposal) {
			if (disposal.version > 1) {
				this.showLogBookFormList = false
                if (disposal.form_data.section_e.transfer_date) {
                    this.entry.disposal_date = disposal.form_data.section_e.transfer_date
                }
				this.contact = {
					contact_id: disposal.contact_id,
					first_name: disposal.form_data.section_b.first_name,
					middle_name: disposal.form_data.section_b.middle_name,
					last_name: disposal.form_data.section_b.last_name,
                    suffix: disposal.form_data.section_b.suffix,
					company_name: disposal.form_data.section_b.company_name,
					address_1: disposal.form_data.section_b.address_1,
					address_2: disposal.form_data.section_b.address_2,
					city: disposal.form_data.section_b.city,
					state: disposal.form_data.section_b.state,
					zip_code: disposal.form_data.section_b.zip_code,
					birthdate: disposal.form_data.section_b.birth_date
				}
				this.entry.city = disposal.form_data.section_b.city
				this.entry.state = disposal.form_data.section_b.state
				this.ffl_log_book_form_id = disposal.ffl_log_book_form_id
				this.entry.form_number = disposal.form_number
                this.entry.notes = ''
                this.entry.nics_number = ''
                if (disposal.nics_number) {
                    this.entry.nics_number = disposal.nics_number
                } else if (disposal.form_data.section_c.nics_buyer_exempt_permit_state_type || disposal.form_data.section_c.nics_buyer_exempt_permit_number) {
                    if (disposal.form_data.section_c.nics_buyer_exempt_permit_state_type) {
                        this.entry.nics_number += disposal.form_data.section_c.nics_buyer_exempt_permit_state_type + ' '
                    }
                    if (disposal.form_data.section_c.nics_buyer_exempt_permit_number) {
                        this.entry.nics_number += disposal.form_data.section_c.nics_buyer_exempt_permit_number
                    }
                }
                if (disposal.form_data.section_c.nics_buyer_background_check || disposal.form_data.section_c.nics_buyer_exempt) {
        			this.nics_not_required = true
    				this.entry.notes += 'NICS not required '
                }
                if (disposal.form_data.section_final && disposal.form_data.section_final.transfer_date) {
				    this.entry.disposal_date = disposal.form_data.section_final.transfer_date
                }
                if (disposal.form_data.section_d.seller_notes) {
		            this.entry.notes += disposal.form_data.section_d.seller_notes
                }
			} else if (disposal.form_data.section_d) {
				this.showLogBookFormList = false
				this.contact = {
					contact_id: disposal.contact_id,
					first_name: disposal.form_data.first_name,
					middle_name: disposal.form_data.middle_name,
					last_name: disposal.form_data.last_name,
					suffix: disposal.form_data.suffix,
					company_name: disposal.form_data.company_name,
					address_1: disposal.form_data.address_1,
					address_2: disposal.form_data.address_2,
					city: disposal.form_data.city,
					state: disposal.form_data.state,
					zip_code: disposal.form_data.zip_code,
					birthdate: disposal.form_data.birth_date
				}
				this.ffl_log_book_form_id = disposal.ffl_log_book_form_id
				this.entry.form_number = disposal.form_number
				this.entry.nics_number = disposal.nics_number || disposal.form_data.section_b.nics_buyer_exempt_permit_number
				this.entry.notes = disposal.form_data.section_d.seller_notes
                if (disposal.form_data.section_final && disposal.form_data.section_final.transfer_date) {
				    this.entry.disposal_date = disposal.form_data.section_final.transfer_date
                }
			} else {
                if (disposal.version > 1) {
		    	    this.modalFor = 'Nothing saved in Section A',
				    this.modalContent = 'Name: ' + this.$fullName(disposal.section_b) + '<br><br>Nothing has been saved in Section A for this 4473.<br>Please complete the form before disposing.'
                } else {
		    	    this.modalFor = 'Nothing saved in Section D',
				    this.modalContent = 'Name: ' + this.$fullName(disposal) + '<br><br>Nothing has been saved in Section D for this 4473.<br>Please complete the form before disposing.'
                }
				this.showModal = true
			}
		},
		searchFormNumbers() {
			this.apiCall({ destination: 'get_list_4473_form_numbers', data: { search_term: this.entry.form_number }}).then(obj => {
				if (obj.status == 'error') {
					this.formNumberSearchMessage = obj.message
				} else {
					if (obj.form_numbers.length > 0) {
						this.form_numbers = obj.form_numbers
					} else {
						this.formNumberSearchMessage = 'No Transaction Serial Numbers found'
					}
				}
			})
		},
		assignFormNumber(formNumber) {
			this.entry.form_number = formNumber
			this.form_numbers = []
		},
		goToForm() {
			this.$router.push('/logbooks/4473/' + this.$route.query.form)
		},
		getListSerialNumbers() {
			this.serial_numbers = [{ serial_number: '' }]
			this.apiCall({ destination: 'get_list_serial_numbers', data: { ffl_log_book_entry_id: this.id }}).then(obj => {
				if (obj.status == 'error') {
					this.disposalSearchMessage = obj.message
				} else {
					this.serial_numbers = obj.serial_numbers
					this.client_id = obj.client_id
				}
			})
		},
		getListLogBookForms() {
			// if there is a query 'form' we're coming from 'Dispose This Form'
            if (this.$route.query.form) {
                this.getDisposeThisForm(this.$route.query.form)
				return
            }
            this.loading = true
            let postData = {
                disposable: 1, form_type: '4473', full_list: 1
            }
            // if (this.$route.query.form) {
            //     postData.ffl_log_book_form_id = this.$route.query.form
            // }
			this.apiCall({ destination: 'get_list_ffl_log_book_forms', data: postData }).then((obj) => {
				if (obj.status == 'success') {
					this.definition = obj.definition
					this.client_id = obj.client_id
                    this.logBookForms = obj.log_book_forms
                    if (obj.log_book_forms.length == 1) {
                        this.assignDisposal(obj.log_book_forms[0], true)
					}
				}
				this.loading = false
			}).catch((err) => {
				console.log(err)
				this.loading = false
			})
		},
		getDisposeThisForm(formId) {
			if (this.loading) { return }
			this.loadingModal = true
			this.modalFor = 'Dispose This Form',
			this.modalContent = "Getting disposed to and serial number info for this form..."
			this.showModal = true
			this.apiCall({ destination: 'get_item_ffl_log_book_form_for_disposal', data: { ffl_log_book_form_id: formId } }).then((obj) => {
				this.loadingModal = false
				if (obj.log_book_form) {
					this.populateDisposal(obj.log_book_form)
				}
				this.modalStatus = obj.status
				if (obj.status == 'success') {
					if (obj.serial_numbers && obj.serial_numbers.length > 0) {
						this.serial_numbers = obj.serial_numbers
					}
					this.loadingModal = false
					this.modalFor = '',
					this.modalContent = ''
					this.showModal = false
				} else {
					this.modalContent = obj.message
				}
			}).catch((err) => {
				console.log(err)
				this.loadingModal = false
			})
		},
		saveDisposition() {
			if (!this.isDisposable || this.dispositionComplete) {
				return false
			}
			// only post non-blank serial numbers
			let postSerialNumbers = []
			for (let i = 0; i < this.serial_numbers.length; i++) {
				if (this.serial_numbers[i]['serial_number'] && this.serial_numbers[i]['ffl_log_book_entry_id']) {
					postSerialNumbers.push(this.serial_numbers[i])
				}
			}
			if (postSerialNumbers.length < 1) {
				return false
			}
            let disposedByNote = 0
            if (this.nics_not_required || (!this.entry.nics_number && !this.entry.repair_number && this.entry.notes)) {
                disposedByNote = 1
            }
			this.apiCall({ destination: 'save_item_ffl_log_book_entry', data: { disposing: 1, entry: this.entry, contact: this.contact, serial_numbers: postSerialNumbers, disposed_by_note: disposedByNote, ffl_log_book_form_id: this.ffl_log_book_form_id } }).then(obj => {
				if (obj.status == 'success') {
					this.saveMessage = "Success! " + obj.message
                    this.dispositionComplete = true
                    if (obj.updated_ffl_log_book_entry_id) {
                        this.entry.ffl_log_book_entry_id = obj.updated_ffl_log_book_entry_id
                    }
				} else {
					this.saveMessage = "Error: " + obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		clearData() {
    		this.entry = {
	    		ffl_log_book_entry_id: null,
	    		disposal_date: '',//this.$today().clone().format('YYYY-MM-DD'),
	    		disposal_contact_id: null,
	    		nics_number: null,
	    		form_number: null,
	    		repair_number: null,
				notes: null
    		}
            this.nics_not_required = ''
            this.reloadDateInput = Math.random()
    		this.serial_numbers = [{ serial_number: '' }]
			this.disposals = []
			this.ffl_log_book_form_id = ''
			this.searchingSerialNumberIndex = 0
			this.serialNumberSearchResults = []
			this.serialNumberSesarchMessage = ''
            this.dispositionComplete = false
			this.resetContact()
		},
		resetContact() {
			this.contact = {
				client_id: null,
				contact_id: null,
				first_name: null,
				last_name: null,
				company_name: null,
				address_1: null,
				address_2: null,
				city: null,
				state: null,
				zip_code: null,
				ffl_number: null,
				notes: [{ note: '', priority: '' }]
			}
		},
		doPrint() {
            if (this.loadingLogBookEntry) {
                return
            }
        	this.loadingLogBookEntry = true
			this.apiCall({ destination: 'get_item_ffl_log_book_entry', data: { ffl_log_book_entry_id: this.entry.ffl_log_book_entry_id, print: true } }).then(obj => {
        		this.loadingLogBookEntry = false
				if (obj.pdf_url) {
                	window.open(obj.pdf_url)
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
        		this.loading = false
			})
		}
	}
}
</script>

<style lang="scss" scoped>
</style>
